import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { rgba } from 'polished'
import { useTheme } from '@emotion/react'
import { Close } from 'chaoskit/src/components'
import { gradient } from 'chaoskit/src/assets/styles/utility'
import { graphql, useStaticQuery } from 'gatsby'

import Data from '~components/Data'
import ButtonLink from '~components/ButtonLink'

const storageKey = 'gremlin__announcementBar'

// Translate variation
const getGradient = (variationOption, theme) => {
  switch (variationOption) {
    case 'dark-blue':
      return gradient.darkerBlueDarkestBlue(theme)
    case 'blue-purple':
      return gradient.darkBluePurple(theme)
    case 'dark-purple':
      return {
        backgroundColor: theme.brand.black,
        backgroundImage: `linear-gradient(to right, ${rgba(
          theme.brand.purple,
          0.075
        )},
           ${rgba(theme.brand.purple, 0)}
          )`,
      }
    default:
      return gradient.blueGreen(theme)
  }
}

const AnnouncementBar = ({ className, ...rest }) => {
  const theme = useTheme()

  const [isVisible, setVisibility] = useState(false)

  const { announcementBar: announcementBarQuery } = useStaticQuery(graphql`
    query AnnouncementBarData {
      announcementBar: file(
        name: { eq: "announcement-bar" }
        absolutePath: { regex: "/src/data/" }
        # Only return something from query if enabled in CMS
        childMdx: { frontmatter: { show: { eq: true } } }
      ) {
        childMdx {
          frontmatter {
            title
            show
            cta {
              label
              show
              url
            }
            blurb
            variation
          }
        }
      }
    }
  `)

  const announcementBarData = announcementBarQuery?.childMdx?.frontmatter

  const setCookie = () => {
    window.localStorage.setItem(storageKey, announcementBarData?.title)

    setVisibility(false)
  }

  // Hide if `show` prop is false, the cookie values don't match, or we've updated the state to set the cookie
  useEffect(() => {
    if (
      announcementBarQuery &&
      announcementBarData?.title !== window.localStorage.getItem(storageKey) &&
      !isVisible
    )
      setVisibility(true)
  }, [announcementBarQuery, announcementBarData?.title, isVisible])

  // If the query returned nothing, bail
  if (!isVisible) return null

  return (
    <aside
      css={[
        getGradient(announcementBarData.variation, theme),
        {
          position: 'relative',
          padding: theme.space.small,
          boxShadow: theme.boxShadow.large,
          zIndex: 1,
          textAlign: 'center',
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          gap: theme.space.small,

          [theme.mq.medium]: {
            alignItems: 'center',
          },
        },
      ]}
      className={clsx('u-contrast', className)}
      {...rest}
    >
      <Close onClick={setCookie} title="Hide announcement" />
      <div
        css={{
          display: 'grid',
          alignItems: 'center',
          justifyContent: 'center',
          gap: theme.space.small,

          [theme.mq.medium]: {
            gridTemplateColumns: 'max-content min-content',
          },
        }}
      >
        <div
          css={{
            ...theme.text.small,
          }}
          className={theme.settings.classes.trim}
        >
          <Data>{announcementBarData.blurb}</Data>
        </div>
        {announcementBarData.cta.show && (
          <div>
            <ButtonLink
              type="default"
              url={announcementBarData.cta.url}
              size="xsmall"
            >
              <Data>{announcementBarData.cta.label}</Data>
            </ButtonLink>
          </div>
        )}
      </div>
    </aside>
  )
}

AnnouncementBar.propTypes = {
  className: PropTypes.string,
}

export default AnnouncementBar
